import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { safeUrl } from "@/lib/landingDefaults";
import { waLink, WhatsAppIcon } from "@/components/WhatsAppButton";
import OfferCard, { formatPrice } from "@/components/OfferCard";
import OfferCarousel from "@/components/OfferCarousel";
import { useBranding, logoSrc } from "@/lib/branding";
import { useMemberMessage, fieldKey, MemberEnquiryContext } from "@/lib/waTemplates";
import { ProductCard, CardTag, cardBtnClass } from "@/components/ProductCard";
import QuickEnquiryForm from "@/components/landing/QuickEnquiry";
import { Gift, Plane, Star, Heart, Headphones, FileCheck2, BedDouble, Instagram, Facebook, Youtube, MapPin,
         Calendar, Users, ArrowRight, Utensils, Car, Ship, Ticket, Handshake, Wallet, CircleCheck, Menu, X } from "lucide-react";

export const ICONS = { gift: Gift, plane: Plane, star: Star, heart: Heart, support: Headphones, visa: FileCheck2,
  hotel: BedDouble, instagram: Instagram, facebook: Facebook, youtube: Youtube, whatsapp: WhatsAppIcon, pin: MapPin,
  calendar: Calendar, users: Users, restaurants: Utensils, hotels: BedDouble, cars: Car, yachts: Ship,
  attractions: Ticket, handshake: Handshake, wallet: Wallet, check: CircleCheck };
const Ico = ({ name, className = "h-5 w-5" }) => { const I = ICONS[(name || "").toLowerCase()] || Star; return <I className={className} />; };
const wrap = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-10";

export function CmsLink({ link, wa, msg, className, children, testid }) {
  if (!link?.url) return null;
  if (link.url === "whatsapp") {
    if (!wa) return null;
    return <a href={waLink(wa, msg || "Hi Zola, I'd like to know more.")} target="_blank" rel="noopener noreferrer"
              className={className} data-testid={testid}>{children}</a>;
  }
  const url = safeUrl(link.url);
  if (url.startsWith("#") && url.length > 1) {
    const jump = (e) => {
      const el = document.getElementById(url.slice(1));
      if (!el) return;
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", url);
    };
    return <a href={`/${url}`} onClick={jump} className={className} data-testid={testid}>{children}</a>;
  }
  if (url.startsWith("/")) return <Link to={url} className={className} data-testid={testid}>{children}</Link>;
  const ext = /^https?:/.test(url);
  return <a href={url} className={className} data-testid={testid} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{children}</a>;
}

const H2 = ({ children, sub }) => (
  <div>
    <h2 className="font-serif-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--navy)" }}>{children}</h2>
    {sub && <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>{sub}</p>}
  </div>
);

function MobileMenu({ items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="md:hidden relative">
      <button type="button" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn"
              className="h-9 w-9 grid place-items-center rounded-lg" style={{ border: "1px solid var(--border)" }}>
        {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
      </button>
      {open && (
        <div className="absolute left-0 top-11 w-48 rounded-xl p-2 shadow-lg z-40" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
             onClick={() => setOpen(false)} data-testid="mobile-menu">
          {items.map((m, i) => <CmsLink key={i} link={m} className="block px-3 py-2 rounded-lg text-sm" testid={`mobile-nav-${i}`}>{m.label}</CmsLink>)}
        </div>
      )}
    </div>
  );
}

export function Header({ c }) {
  const branding = useBranding();
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md" data-testid="landing-header"
            style={{ background: "color-mix(in oklab, var(--bg) 88%, transparent)", borderBottom: "1px solid var(--border)" }}>
      <div className={`${wrap} h-16 flex items-center justify-between gap-4`}>
        <a href="#top"><img src={logoSrc(branding.logo_light)} alt="Zola Safari Tours" className="h-11 sm:h-12 w-auto" data-testid="header-logo" /></a>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
          {c.menu.map((m, i) => <CmsLink key={i} link={m} className="hover:text-[color:var(--gold)] transition-colors" testid={`nav-${i}`}>{m.label}</CmsLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <MobileMenu items={c.menu} />
          <CmsLink link={c.login} className="h-9 px-4 sm:px-5 inline-flex items-center rounded-lg text-sm font-medium" testid="header-login">
            <span className="rounded-lg border px-4 py-1.5" style={{ borderColor: "var(--navy)", color: "var(--navy)" }}>{c.login.label}</span>
          </CmsLink>
          <CmsLink link={c.join} className="h-9 px-4 sm:px-5 inline-flex items-center rounded-lg text-sm font-semibold bg-navy text-white whitespace-nowrap" testid="join-cta">
            {c.join.label}
          </CmsLink>
        </div>
      </div>
    </header>
  );
}

export function Hero({ c }) {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="section-hero">
      <img src={c.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(8,32,64,0.85) 0%, rgba(8,32,64,0.55) 45%, rgba(8,32,64,0.05) 75%)" }} />
      <div className={`${wrap} relative py-14 sm:py-20 text-white max-w-7xl`}>
        <div className="text-sm sm:text-base opacity-90">{c.tagline}</div>
        <h1 className="font-serif-display mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
          {c.headline1}<br /><span style={{ color: "var(--gold)" }}>{c.headline2}</span>
        </h1>
        <p className="mt-4 max-w-md text-sm sm:text-base opacity-95">{c.subtext}</p>
        <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
          {c.chips.map((ch, i) => (
            <div key={i} className={`flex items-center gap-2 text-sm ${i ? "sm:pl-6 sm:border-l border-white/30" : ""}`}>
              <Ico name={ch.icon} className="h-6 w-6" /><span className="max-w-[80px] leading-tight">{ch.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function QuickEnquiry({ c, wa }) {
  return <QuickEnquiryForm c={c} wa={wa} wrap={wrap} H2={H2} Ico={Ico} />;
}

function useLinkedDeal(id) {
  const [state, setState] = useState({ id: null, deal: null });
  useEffect(() => {
    if (!id) return;
    api.get(`/public/offers/${id}`).then(({ data }) => setState({ id, deal: data })).catch(() => setState({ id, deal: null }));
  }, [id]);
  return { deal: id && state.id === id ? state.deal : null, loading: !!id && state.id !== id };
}

function useFeaturedOffers(place) {
  const [state, setState] = useState({ loaded: false, offers: [] });
  useEffect(() => {
    api.get("/public/featured", { params: { place } }).then(({ data }) => setState({ loaded: true, offers: Array.isArray(data) ? data : data ? [data] : [] }))
      .catch(() => setState({ loaded: true, offers: [] }));
  }, [place]);
  return state;
}

const offerImg = (o) => (o?.image_url ? `${API.replace(/\/api$/, "")}${o.image_url}` : null);

function FeaturedCard({ c, wa, f, deal, memberMsg, testid }) {
  const src = f || deal;
  const partner = src?.partner_name || (f ? f.title : c.title);
  const offer = src?.title || c.seal || c.title;
  const image = f ? offerImg(f) : (c.image || offerImg(deal));
  const desc = f ? (f.featured_subtitle || f.description) : (c.subtitle || deal?.description);
  const tag = src?.discount || f?.featured_seal || c.seal;
  const sfx = f ? `-${f.id}` : "";
  const cta = memberMsg ? (wa && (
    <a href={waLink(wa, memberMsg({ offer, partner }))} target="_blank" rel="noopener noreferrer" data-testid={`featured-claim-btn${sfx}`} className={cardBtnClass}>
      <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> {f?.featured_cta || c.memberCta || "Claim Offer"}
    </a>
  )) : (
    <Link to="/join" data-testid={`featured-join-btn${sfx}`} className={cardBtnClass.replace("h-9", "min-h-[36px] py-1.5 px-2").replace("whitespace-nowrap", "text-center leading-tight")}>
      {c.guestCta || "Join to Unlock Member Rates"} <ArrowRight className="h-4 w-4 flex-shrink-0" />
    </Link>
  );
  return (
    <ProductCard image={image} title={partner} description={desc} testid={testid}
      price={src ? formatPrice(src) : null} priceTestid={`featured-price${sfx}`}
      badge={src && (src.partner_name || !f) && <div className="mb-1 text-[11px] font-semibold line-clamp-1" style={{ color: "var(--gold)" }} data-testid={`featured-deal-title${sfx}`}>{src.title}</div>}
      tagRight={tag && <CardTag gold={!!src?.discount} testid={`featured-tag${sfx}`}>
        {!src?.discount && <Star className="h-3 w-3 flex-shrink-0" style={{ color: "var(--gold)" }} />}{tag}</CardTag>}
      action={cta} />
  );
}

export function Featured({ c, wa }) {
  const memberMsg = useMemberMessage("featured");
  const inPortal = !!useContext(MemberEnquiryContext);
  const fo = useFeaturedOffers(inPortal ? "customer_portal" : "landing_page");
  const { deal, loading } = useLinkedDeal(fo.loaded && !fo.offers.length ? c.deal_id : null);
  if (!fo.loaded || loading) return null;
  const many = fo.offers.length > 0;
  return (
    <section id="featured" className={`${wrap} mt-10`} data-testid="section-featured" data-source={many ? "offer" : deal ? "linked-deal" : "manual"}>
      <OfferCarousel testid="featured-carousel" header={c.badge && <H2>{c.badge}</H2>}>
        {many
          ? fo.offers.map((f) => <FeaturedCard key={f.id} c={c} wa={wa} f={f} memberMsg={memberMsg} testid={`featured-card-${f.id}`} />)
          : <FeaturedCard c={c} wa={wa} deal={deal} memberMsg={memberMsg} testid="featured-card" />}
      </OfferCarousel>
    </section>
  );
}

export function Packages({ c, wa, embedded }) {
  const memberMsg = useMemberMessage("package");
  const items = c.items.filter((p) => p.published !== false);
  if (!items.length) return null;
  return (
    <section id="packages" className={`${wrap} mt-14`} data-testid="section-packages">
      <OfferCarousel testid="packages-carousel" header={
        <div className="flex items-end justify-between gap-3"><H2 sub={c.subtitle}>{c.title}</H2>
          {!embedded && c.viewAll?.label && <CmsLink link={c.viewAll} className="text-sm whitespace-nowrap inline-flex items-center gap-1 md:hidden" testid="packages-view-all">{c.viewAll.label} <ArrowRight className="h-4 w-4" /></CmsLink>}
        </div>}>
        {items.map((p) => (
          <ProductCard key={p.id} testid={`package-${p.id}`} image={p.image} title={p.title} description={p.desc}
            price={p.price ? `${p.currency} ${Number(p.price).toLocaleString()}` : null} priceSub={p.per}
            action={wa && (
              <a href={waLink(wa, memberMsg
                ? memberMsg({ title: p.title, price: p.price ? `${p.currency} ${Number(p.price).toLocaleString()} ${p.per || ""}`.trim() : "" })
                : `Hi Zola, I'm interested in the ${p.title} package${p.price ? ` (${p.currency} ${Number(p.price).toLocaleString()} ${p.per})` : ""}. Could you share more details?`)}
                 target="_blank" rel="noopener noreferrer" data-testid={`package-enquire-${p.id}`} className={cardBtnClass}>
                <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> {c.button}</a>
            )} />
        ))}
      </OfferCarousel>
    </section>
  );
}

export function Deals({ c, wa }) {
  const [offers, setOffers] = useState([]);
  const [cat, setCat] = useState("");
  useEffect(() => { api.get("/public/offers").then(({ data }) => setOffers(data)).catch(() => {}); }, []);
  const list = useMemo(() => {
    const spot = offers.find((o) => o.spotlight);
    return [...(spot ? [spot] : []), ...offers.filter((o) => o !== spot)].filter((o) => !cat || o.category === cat);
  }, [offers, cat]);
  return (
    <section id="deals" className={`${wrap} mt-14`} data-testid="section-deals">
      <OfferCarousel testid="landing-offers-carousel" header={
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3"><H2 sub={c.subtitle}>{c.title}</H2>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="deal-filters">
            {["", ...c.categories].map((k) => (
              <button key={k || "all"} type="button" onClick={() => setCat(k)} data-testid={`deal-filter-${k || "all"}`}
                      className={`h-8 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 whitespace-nowrap ${cat === k ? "bg-navy text-white" : ""}`}
                      style={cat === k ? {} : { border: "1px solid var(--border)" }}>
                {k && <Ico name={k} className="h-3.5 w-3.5" />}{k || "All"}
              </button>
            ))}
          </div>
        </div>}>
        {list.length ? list.map((o) => <OfferCard key={o.id} offer={o} source="landing_page" waNumber={wa} spotlight={!!o.spotlight} />)
          : <div className="col-span-full text-sm py-8" style={{ color: "var(--text-muted)" }} data-testid="deals-empty">
              {offers.length ? "No deals in this category yet." : "New partner offers are coming soon."}</div>}
      </OfferCarousel>
    </section>
  );
}

export function WhyJoin({ c }) {
  return (
    <section id="why" className="mt-14 relative overflow-hidden" style={{ background: "var(--bg-soft)" }} data-testid="section-why">
      <img src={c.image} alt="" loading="lazy" className="hidden md:block absolute right-0 top-0 h-full w-[38%] object-cover"
           style={{ maskImage: "linear-gradient(90deg, transparent, #000 35%)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 35%)" }} />
      {c.script && <div className="hidden md:block absolute right-8 top-8 font-script text-3xl text-white drop-shadow max-w-[180px] text-right">{c.script}</div>}
      <div className={`${wrap} relative py-10`}>
        <div className="md:max-w-[60%]"><H2 sub={c.subtitle}>{c.title}</H2>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {c.items.map((it, i) => (
              <div key={i} className={`${i ? "lg:border-l lg:pl-5" : ""}`} style={{ borderColor: "var(--border)" }}>
                <Ico name={it.icon} className="h-6 w-6" /><div className="mt-2 font-semibold text-sm">{it.title}</div>
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{it.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CustomPlan({ c, wa }) {
  const memberMsg = useMemberMessage("custom");
  return (
    <section className={`${wrap} mt-8`} data-testid="section-custom">
      <div className="rounded-2xl overflow-hidden grid md:grid-cols-[260px_1fr_220px] items-center gap-5" style={{ background: "#fbf4e8" }}>
        <img src={c.image} alt="" loading="lazy" className="h-44 md:h-full w-full object-cover" />
        <div className="px-5 md:px-0 py-2">
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--gold)" }}>{c.eyebrow}</div>
          <h3 className="font-serif-display text-xl sm:text-2xl font-bold mt-1" style={{ color: "var(--navy)" }}>{c.title}</h3>
          <p className="text-sm mt-2" style={{ color: "#4b5563" }}>{c.text}</p>
          <CmsLink link={c.button} wa={wa} msg={memberMsg ? memberMsg({ title: c.title }) : "Hi Zola, I'd like a custom holiday plan. Destination: , Dates: , Travellers: , Budget: "} testid="custom-plan-btn"
                   className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-lg text-sm font-semibold bg-navy text-white">
            {c.button.label} <ArrowRight className="h-4 w-4" /></CmsLink>
        </div>
        <ul className="px-5 pb-5 md:p-0 md:pr-6 space-y-2 text-sm md:border-l md:pl-6" style={{ color: "#1f2937", borderColor: "#e9dcc5" }}>
          {c.bullets.map((b, i) => <li key={i} className="flex items-center gap-2"><Ico name={["pin", "calendar", "users", "wallet"][i] || "check"} className="h-4 w-4" />{b}</li>)}
        </ul>
      </div>
    </section>
  );
}

export function Partner({ c, wa }) {
  return (
    <section className={`${wrap} mt-6`} data-testid="section-partner">
      <div className="rounded-2xl overflow-hidden grid md:grid-cols-[200px_1fr_auto] items-center gap-5" style={{ background: "#eef6f2" }}>
        <img src={c.image} alt="" loading="lazy" className="h-36 md:h-full w-full object-cover" />
        <div className="px-5 md:px-0 py-2">
          <h3 className="font-serif-display text-xl sm:text-2xl font-bold" style={{ color: "var(--navy)" }}>{c.title}</h3>
          <p className="text-sm mt-2" style={{ color: "#374151" }}>{c.text}</p>
          <p className="text-xs mt-1" style={{ color: "#6b7280" }}>{c.subtext}</p>
        </div>
        <div className="px-5 pb-5 md:p-6 flex flex-col items-start md:items-end gap-4">
          <CmsLink link={c.button} wa={wa} msg="Hi Zola, I'd like to become a Zola Rewards partner." testid="partner-btn"
                   className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold" >
            <span className="inline-flex items-center gap-2 h-10 px-5 rounded-full" style={{ border: "1px solid var(--navy)", color: "var(--navy)" }}>{c.button.label} <ArrowRight className="h-4 w-4" /></span>
          </CmsLink>
          <div className="flex flex-wrap gap-4 text-xs" style={{ color: "#374151" }}>
            {c.chips.map((ch, i) => <span key={i} className="inline-flex items-center gap-1.5"><Ico name={ch} className="h-4 w-4" />{ch}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer({ c, wa }) {
  const branding = useBranding();
  return (
    <footer className="mt-14 text-white" style={{ background: "#003c53" }} data-testid="landing-footer">
      <div className={`${wrap} py-8 grid gap-6 md:grid-cols-[auto_1fr_auto] items-start`}>
        <img src={logoSrc(branding.logo_dark)} alt="Zola Safari Tours" className="h-16 w-auto" data-testid="footer-logo" />
        <nav className="flex flex-wrap gap-x-8 gap-y-2 text-sm md:justify-center">
          {c.links.map((l, i) => <CmsLink key={i} link={l} wa={wa} className="hover:underline" testid={`footer-link-${i}`}>{l.label}</CmsLink>)}
        </nav>
        <div className="flex gap-4">
          {c.socials.map((s, i) => <CmsLink key={i} link={s} wa={wa} className="opacity-90 hover:opacity-100" testid={`footer-social-${s.icon}`}><Ico name={s.icon} className="h-5 w-5" /></CmsLink>)}
        </div>
      </div>
      <div className={`${wrap} pb-6 text-xs space-y-1`}>
        {c.company && <div className="text-sm font-semibold mb-1.5" data-testid="footer-company">{c.company}</div>}
        <div className="space-y-1 opacity-80">{c.addresses.map((a, i) => <div key={i}>{a}</div>)}</div>
      </div>
      <div className="border-t border-white/10">
        <div className={`${wrap} py-4 pr-20 sm:pr-0 flex flex-col sm:flex-row justify-between gap-2 text-xs opacity-90`}>
          <span>{(c.copyright || "").replace(/\b20\d\d\b/, String(new Date().getFullYear()))}</span>
          <span className="flex gap-6"><CmsLink link={c.terms} className="hover:underline">{c.terms.label}</CmsLink>
            <CmsLink link={c.privacy} className="hover:underline">{c.privacy.label}</CmsLink></span>
        </div>
      </div>
    </footer>
  );
}
